import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { RefObject } from 'react';
import { NumberInput, Select, TextInput } from '../../../form';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components } from 'formiojs';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { EhsFormioReactComponent, formatNumberToDecimalPlaces } from '../';
import { intl } from '../../../../lib';
import type { Component } from '@formio/react';
import { useQuestionnaireValidation } from '../../questionnaire/useQuestionnaireValidation';
import { HBox } from '../../../layout';
import type { LanguageProperty } from '../../../generated/LanguageProperty';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
type UnitOption = {
  unitLabel: string;
  unitValue: string;
};

export type EhsFormioQuantityConstructor = {
  classNames: string;
  calculateValue: string;
  decimalLimit?: number;
  unitOptions: Array<UnitOption>;
};

export type EhsFormioQuantitySettings = {
  classNames?: string;
  calculateValue?: string;
  decimalPlaces: number;
  readonly unitOptions: Array<UnitOption>;
};

const NUMBER_UNIT_DELIMITER = '^^^';

export const EhsFormioQuantityType = 'ehsQuantity';
export class EhsFormioQuantity extends EhsFormioReactComponent<
  EhsFormioQuantitySettings,
  EhsFormioQuantityConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioQuantityType,
      label: 'common.unknown',
      key: EhsFormioQuantityType,
      classNames: '',
      calculateValue: '',
      selectOptions: [],
      ...extend
    });
  }
  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-NumberInput',
      group: 'advanced',
      icon: 'hashtag',
      documentation: '/developers/custom-components',
      weight: 50,
      schema: EhsFormioQuantity.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Add only valid translation keys'
        },
        {
          type: 'textfield',
          key: 'classNames',
          label: 'classNames',
          tooltip:
            'A space-separated string that will be applied as class names.',
          weight: 30
        },
        {
          type: 'textarea',
          input: true,
          key: 'calculateValue',
          label: 'Calculated values',
          editor: 'ace',
          as: 'javascript',
          rows: 2,
          weight: 40,
          tooltip:
            'Enter custom javascript code, i.e: value = data.a + data.b + data.c; [Available Variables: token, form, submission, data, row, component, instance, value, moment, _, utils, util]'
        }
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private classNames = '';
  private calculateValue = '';
  private decimalPlaces = 0;
  private unitOptions = [{ unitLabel: '', unitValue: '' }];
  extractFields(component: Component<EhsFormioQuantityConstructor>): void {
    this.classNames = component.classNames;
    this.calculateValue = component.calculateValue;
    this.shouldSetValue = true;
    if (!component.calculateValue) {
      this.dataForSetting = this._data[component.key as string];
    }
    this.unitOptions = component.unitOptions;
    this.decimalPlaces = component.decimalLimit ?? 0;
  }

  attachEhsReact(): [
    (
      props: EhsFormioReactProps<EhsFormioQuantitySettings>
    ) => React.JSX.Element,
    EhsFormioQuantitySettings
  ] {
    return [
      QuantityInputCmp,
      {
        classNames: this.classNames,
        calculateValue: this.calculateValue,
        decimalPlaces: this.decimalPlaces,
        unitOptions: this.unitOptions
      }
    ];
  }
}
//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------
function extractQuantity(value: string): string {
  const quantity = value.split(NUMBER_UNIT_DELIMITER)[0];
  if (!quantity) {
    return '';
  }

  return quantity;
}

function extractUnit(value: string): string {
  const unit = value.split(NUMBER_UNIT_DELIMITER)[1];
  if (!unit) {
    return '';
  }
  return unit.replaceAll('\\', '');
}

/**
 * The Logic for the Formio-Component. For the component we use string as a value for formio. In order the validation to be active we use pattern validation that formio provide use.
 * Therefore, we need to somehow encode the number value and unit value into the string. We use it in the format:
 * "155^^^kg" that would represent 15 kg and ^^^ is a delimiter between the number value and the unit.
 *
 * @param params formio {@link EhsFormioQuantitySettings} properties
 */
function QuantityInputCmp({
  value,
  onChange,
  label,
  readOnly,
  required,
  defaultValue,
  settings,
  keyForComponent
}: EhsFormioReactProps<EhsFormioQuantitySettings>): React.JSX.Element {
  const { errorMessage, isQuestionnaire } = useQuestionnaireValidation(
    keyForComponent
  );
  const [number, setNumber] = useState<string>(
    extractQuantity(defaultValue ?? '')
  );
  const [unit, setUnit] = useState<string>(extractUnit(defaultValue ?? ''));

  const onNumberInputChange = useCallback(
    (value?: number) => {
      const newNumber = value
        ? formatNumberToDecimalPlaces(value, settings.decimalPlaces)
        : '';
      setNumber(newNumber);

      onChange(`${newNumber}${NUMBER_UNIT_DELIMITER}${unit}`);
    },
    [onChange, settings.decimalPlaces, unit]
  );

  const onSelectValueChange = useCallback(
    (unitOption: UnitOption | null) => {
      const newUnit = unitOption?.unitValue ?? '';
      setUnit(newUnit);

      onChange(`${number}${NUMBER_UNIT_DELIMITER}${newUnit}`);
    },
    [number, onChange]
  );

  const numberValueWrapper = useMemo(() => {
    if (!value) {
      return undefined;
    }

    const numberWrapper = extractQuantity(value);
    if (!numberWrapper) {
      return undefined;
    }
    return isNaN(Number(numberWrapper)) ? undefined : Number(numberWrapper);
  }, [value]);

  const unitValueWrapper = useMemo(() => {
    if (!value) {
      return null;
    }
    const unitValue = extractUnit(value);
    if (!unitValue) {
      return null;
    }
    return (
      settings.unitOptions.find(unit => unit.unitValue === unitValue) ?? null
    );
  }, [settings.unitOptions, value]);

  const container = useRef<HTMLDivElement>(null);

  return useMemo(() => {
    return (
      <HBox
        className={`ehs-quantity ${settings.classNames}`}
        align="flex-end"
        ref={container}
      >
        <NumberInput
          decimalPlaces={settings.decimalPlaces}
          onChange={onNumberInputChange}
          value={numberValueWrapper}
          label={label}
          readOnly={!!(readOnly || settings.calculateValue)}
          className="ehs-quantity-value"
          error={
            errorMessage && !numberValueWrapper
              ? intl('formio.quantity.value')
              : ''
          }
          required={required}
          labelOnTop={isQuestionnaire}
        />
        <QuantitySelectComponent
          container={container}
          value={unitValueWrapper}
          onUnitChange={onSelectValueChange}
          settings={settings}
          readOnly={readOnly}
          required={required}
          isQuestionnaire={isQuestionnaire}
          errorMessage={errorMessage}
        />
      </HBox>
    );
  }, [
    numberValueWrapper,
    onNumberInputChange,
    label,
    settings,
    readOnly,
    required,
    isQuestionnaire,
    errorMessage,
    onSelectValueChange,
    unitValueWrapper
  ]);
}

type QuantitySelectComponentProps = Pick<
  EhsFormioReactProps<EhsFormioQuantitySettings>,
  'settings' | 'readOnly' | 'required'
> & {
  value: UnitOption | null;
  onUnitChange: (unit: UnitOption | null) => void;
  isQuestionnaire: boolean;
  errorMessage: string;
  container?: RefObject<HTMLElement | null>;
};
function QuantitySelectComponent({
  settings,
  isQuestionnaire,
  onUnitChange,
  errorMessage,
  value,
  readOnly,
  required,
  container
}: QuantitySelectComponentProps) {
  const translatedItems = useMemo(() => {
    return settings.unitOptions.map(value => {
      return {
        unitValue: value.unitValue,
        unitLabel: intl(value.unitLabel as LanguageProperty, value.unitLabel)
      };
    });
  }, [settings.unitOptions]);

  useEffect(() => {
    if (translatedItems.length === 1) {
      onUnitChange(translatedItems[0]);
    }
  }, [onUnitChange, translatedItems]);

  return useMemo(() => {
    if (translatedItems.length === 1) {
      return (
        <TextInput
          className="ehs-quantity-unit-text"
          type="text"
          label={intl('common.unit.capitalized')}
          labelOnTop={isQuestionnaire}
          value={translatedItems[0].unitValue}
          readOnly
        />
      );
    }

    return (
      <Select
        tooltipContainerRef={container}
        className="ehs-quantity-unit-select"
        variant="single"
        items={translatedItems}
        itemValueProp="unitValue"
        itemLabelProp="unitValue" // for now, we only use value.
        label={intl('common.unit.capitalized')}
        labelOnTop={isQuestionnaire}
        value={value}
        onChange={onUnitChange}
        readOnly={readOnly}
        required={required}
        error={errorMessage && !value ? intl('formio.quantity.unit') : ''}
      />
    );
  }, [
    container,
    errorMessage,
    isQuestionnaire,
    onUnitChange,
    readOnly,
    required,
    translatedItems,
    value
  ]);
}
